/* eslint-disable no-param-reassign */
/* eslint-disable object-curly-newline */
/* eslint-disable no-tabs */
/* eslint-disable react/display-name */
import { isEmpty, isUndefined } from 'lodash';
import { ConvertibleNoteModel, LiqPrefLedgerConfig, LiqPrefLedgerItem, SecurityModel } from 'api';
import * as CAPTABLE from 'common/constants/cap-table';
import { PERCENTAGE, STRING } from 'common/constants/gridType';
import * as inputOptions from 'common/constants/inputs';
import * as validationMessages from 'common/constants/messages/validations';
import {
  COMMON_STOCK as COMMON,
  CONVERTIBLE_NOTES as CONVERTIBLE,
  LIST,
  OPTION,
  PREFERRED_STOCK as PREFERRED,
  UNISSUED_OPTIONS as UNISSUED,
  WARRANT,
} from 'common/constants/securityTypes';
import { capTableTwoDecimalPercentFormat } from 'common/formats/formats';
import { TableData } from 'common/types/scalarSpreadsheet';
import { SelectValueViewer } from 'components';
import { GridDate, GridDateEditor, GridDateValueViewer, GridSelect } from 'components/FeaturedSpreadsheet/components';
import { GridDialogValueViewer } from 'components/Grid';
import VariableValueViewer from 'components/ScalarSpreadsheet/utilities/variable-value-viewer/VariableValueViewer';
import { CellValue, ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { seniorityOptions } from 'pages/CapTable/cap-table/utilities/getOptionsForSelect';
import { addClassName, removeClassName, removeValidationClasses, toString } from 'utilities';
import { getOptions, getSecurityType } from './underlyingSecurities';
import {
  ConvertibleNote,
  DividendRateLedger,
  LiquidationPriority,
  MultipleInvestmentDates,
  OptionsLedger,
} from '../components';
import { VARIABLE_VALUE } from './variablePropertiesUtils';
import { AIFindingValueViewer } from '../components/ai-security-finding-popover';
import AIMergeValueViewer from '../components/ai-security-finding-popover/AIMergeValueViewer';
import { CapTableCell, CapTableCellWithReferences } from '../components/captable-table/CaptableTable.props';
import CitationValueViewer from '../components/citation-value-viewer/CitationValueViewer';
import { PREFERRED_STOCK_VALUE } from '../components/convertible-notes-ledger/config/constants';
import { ConvertibleNotesLedgerCell } from '../components/convertible-notes-ledger/types';
import LiquidationPreferenceLedgerDialog from '../components/liquidation-preference-ledger/LiquidationPreferenceLedger';
import { MultipleInvestmentDatesCell } from '../components/multiple-investments/types';
import { OptionsLedgerCell } from '../components/options-ledger/types';

interface CapTableEnhancedModel extends CapTableCell {
  securityRef: string;
  dividend_rate_ledger_items: any[];
}

const conditions = ({
  cell,
  cells,
  tableData,
}: {
  cell: CapTableCellWithReferences;
  cells: any;
  tableData: TableData<CapTableEnhancedModel> & {
    securities: SecurityModel[];
    has_dividend_rate_ledger: boolean;
    has_liquidation_preference_ledger: boolean;
  };
}) => {
  const { captable: capCells } = cells as { captable: Record<string, CapTableCellWithReferences> };
  const { alias, columnLegend, className, value, columnId, defaultValue, tooltipMessages = [] } = cell;
  const underlyingSecurityCell = capCells[columnLegend! + CAPTABLE.UNDERLYING_SECURITY_ROW_NUMBER];
  const hasDividendsCell = capCells[columnLegend! + CAPTABLE.CUMULATIVE_DIVIDENDS_ROW_NUMBER] || '';
  const hasDividends = Boolean(Number(hasDividendsCell.value));
  const hasMultipleInvestmentsCell = capCells[columnLegend! + CAPTABLE.HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER] || '';
  const hasMultipleInvestments = Boolean(Number(hasMultipleInvestmentsCell.value));
  const hasDivRateLedgerItems = Boolean(
    tableData.columns.find(({ security_ref }) => cell.columnRef === security_ref)?.dividend_rate_ledger_items?.length
  );
  const { isDisabled } = tableData;
  const getValue = () => (defaultValue && !Number.isNaN(defaultValue) ? Number(defaultValue) : (defaultValue ?? ''));

  const resetCellConfig = () => {
    cell.hidden = true;
    cell.readOnly = true;
    cell.forceComponent = false;
    cell.disableEvents = false;
    cell.component = null;
    cell.dialog = null;
    cell.greaterThan = null;
    cell.isDisabled = isDisabled;
  };

  const setAsRequired = (isRequired = true) => {
    cell.isRequired = isRequired;
  };

  const setAsEditable = () => {
    resetCellConfig();
    cell.readOnly = isDisabled;
    cell.hidden = false;
  };

  const setAsReadOnly = () => {
    resetCellConfig();
    cell.readOnly = true;
    cell.hidden = false;
  };

  const setAsHidden = (keepValue = false) => {
    resetCellConfig();
    if (!keepValue) {
      cell.value = getValue() as string;
      cell.expr = cell.value;
    }
    cell.className = removeClassName(removeValidationClasses(className ?? ''), 'read-only--white');
    cell.tooltipMessages = null;
    cell.isRequired = false;
    cell.hidden = true;
  };

  const addTooltipMessage = (messageToAdd: string) => {
    if (!tooltipMessages?.includes(messageToAdd)) {
      tooltipMessages?.push(messageToAdd);
    }

    cell.tooltipMessages = tooltipMessages;
  };

  const removeTooltipMessage = (messageToDelete: string) => {
    if (tooltipMessages?.includes(messageToDelete)) {
      const index = tooltipMessages?.findIndex(message => message === messageToDelete);
      tooltipMessages?.splice(index, 1);
    }
  };

  const isSecurityType = (type: number) => {
    const securityType = capCells[cell.columnLegend! + CAPTABLE.SECURITY_TYPE_ROW_NUMBER];

    if (type && securityType?.value) {
      return securityType.value.toString() === type.toString();
    }
    return false;
  };

  const isUnderlyingSecurity = (type: number) => {
    const securityType = getSecurityType(underlyingSecurityCell.value as string, capCells);

    if (securityType && type) {
      return securityType.toString() === type.toString();
    }
    return false;
  };

  const enableMultipleInvestments = () => {
    setAsReadOnly();
    cell.minValue = 1;
    cell.dialog = {
      content: <MultipleInvestmentDates cell={cell as MultipleInvestmentDatesCell} closeDialog={() => {}} />,
    };
    cell.disableEvents = true;
  };

  const getConvertibleNotes = () => {
    const columnConvertibleNotes: Record<string, CellValue & ConvertibleNoteModel[]> = {};
    const cellsArray = Object.values(capCells).filter(c => !c.totalKey);
    cellsArray.forEach((itemCell: CapTableCell) => {
      if (
        itemCell?.alias === CAPTABLE.CONVERTIBLE_NOTES_ALIAS &&
        (itemCell?.value as any[]).length > 0 &&
        isUndefined(columnConvertibleNotes[itemCell.columnLegend!])
      ) {
        columnConvertibleNotes[itemCell.columnLegend!] = itemCell.value as ConvertibleNoteModel[];
      }
    });
    return columnConvertibleNotes;
  };

  const hasConvertibleNoteAsPreferred = () => {
    let convertibleAsPreferred = false;

    const convertibleNotes = getConvertibleNotes();

    if (!isEmpty(capCells) && !isEmpty(convertibleNotes)) {
      const columnConvertibleNotes = convertibleNotes[cell.columnLegend!];
      convertibleAsPreferred =
        columnConvertibleNotes?.filter(item => item.model_as_equity === PREFERRED_STOCK_VALUE && !item.is_deleted)
          .length > 0;
    }
    return convertibleAsPreferred;
  };

  if (cell.exprBuilderEnabled && cell.expr === '=') {
    cell.minValue = null;
    cell.tooltipMessages = ['Cell in reference state'];
    return cell;
  }
  if (cell.exprBuilderEnabled && cell.expr !== '=') {
    cell.tooltipMessages = null;
  }

  if (cell.mappingReferences && cell.mappingReferences.length > 0) {
    cell.className = addClassName(cell.className ?? '', 'cell-with-references');
  }



  // 1. Security Name
  if (alias === CAPTABLE.SECURITY_NAME_ALIAS) {
    if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      // preferred doesn't have multiple strike price -> no ledger no virtual column
      if (isUnderlyingSecurity(COMMON) || isUnderlyingSecurity(PREFERRED)) {
        if (!columnId && columnId !== 0) {
          setAsReadOnly();
          addTooltipMessage('Title of a virtual column is read-only');
          cell.className = removeClassName(className ?? '', 'text-blue');
          cell.forceComponent = true;
          cell.disableEvents = true;
          cell.tooltipMessages = tooltipMessages;
        } else {
          setAsEditable();
        }
      }
    }
  }

  // 02.  Investment Date
  if (alias === CAPTABLE.INVESTMENT_DATE_ALIAS) {
    if (isSecurityType(PREFERRED) || isSecurityType(CONVERTIBLE)) {
      if (hasMultipleInvestments) {
        enableMultipleInvestments();
      }

      if (!hasDividends || !hasMultipleInvestments) {
        setAsEditable();
        cell.gridDateComponent = GridDate as any;
        cell.dataEditor = (props: any) => <GridDateEditor {...props} />;
        cell.valueViewer = (props: any) => <GridDateValueViewer {...props} />;
        cell.isRequired = true;
      }

      cell.isRequired = true;
    } else {
      setAsHidden();
    }
  }

  // 03.  Security Type
  if (alias === CAPTABLE.SECURITY_TYPE_ALIAS) {
    cell.required = true;
  }

  // 04.  Original Issue Price
  if (alias === CAPTABLE.ISSUE_PRICE_ALIAS) {
    if (isSecurityType(PREFERRED)) {
      // editable
      setAsEditable();
      setAsRequired(true);
      cell.greaterThan = 0;
      cell.skipInitialValidation = true;
    } else if (isSecurityType(CONVERTIBLE)) {
      cell.readOnly = true;
      cell.hidden = true;
      setAsRequired(false);
    } else {
      // hidden
      setAsHidden();
      setAsRequired(false);
      cell.minValue = null;
    }
  }

  // 05.  Shares Outstanding
  if (alias === CAPTABLE.SHARES_OUTSTANDING_ALIAS) {
    if (isSecurityType(UNISSUED)) {
      setAsHidden();
      setAsRequired(false);
    } else if (isSecurityType(PREFERRED)) {
      // Show ledger if has multiple investment dates
      if (hasMultipleInvestments) {
        enableMultipleInvestments();
      }

      if (!hasDividends || !hasMultipleInvestments) {
        setAsEditable();
        setAsRequired(true);
        cell.minValue = 1;
        cell.dialog = null;
      }
    } else if (isSecurityType(COMMON)) {
      setAsEditable();
      setAsRequired(true);
      cell.minValue = 1;
    } else if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(COMMON)) {
        if (!columnId && columnId !== 0) {
          addTooltipMessage('This cell is part of a virtual column');
        }

        setAsReadOnly();
        cell.minValue = 1;
        cell.dialog = {
          content: <OptionsLedger cell={cell as OptionsLedgerCell} closeDialog={() => {}} />,
        };
        cell.disableEvents = true;
        cell.tooltipMessages = tooltipMessages;
        cell.readOnly = isDisabled;
      }

      if (isUnderlyingSecurity(PREFERRED)) {
        setAsEditable();
        setAsRequired(true);
        cell.minValue = 1;
      }

      if (!isUnderlyingSecurity(PREFERRED) && !isUnderlyingSecurity(COMMON)) {
        setAsEditable();
        setAsRequired(true);
        cell.minValue = null;
      }
    } else {
      setAsHidden(true);
      setAsRequired(false);
    }
  }

  // 06.  Conversion Rate
  if (alias === CAPTABLE.CONVERSION_RATE_ALIAS) {
    if (isSecurityType(PREFERRED)) {
      setAsEditable();
      setAsRequired();
    } else if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(PREFERRED)) {
        setAsReadOnly();
      } else if (isUnderlyingSecurity(COMMON)) {
        setAsHidden();
        cell.expr = 1;
        cell.value = 1;
      }
      if (!underlyingSecurityCell.value) {
        setAsHidden();
      }
    } else if (isSecurityType(COMMON)) {
      setAsHidden();
      cell.expr = 1;
      cell.value = 1;
    } else if (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred()) {
      setAsReadOnly();
      cell.expr = 1;
      cell.value = 1;
    } else {
      setAsHidden();
    }
  }

  // 07.  PIK Shares as of today
  if (alias === CAPTABLE.PIK_SHARES_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      if (hasDividends) {
        const dividendPaymentTypeKey = columnLegend! + CAPTABLE.DIVIDEND_PAYMENT_TYPE_ROW_NUMBER;
        const dividendPaymentType = capCells[dividendPaymentTypeKey];
        const isCash = Number(dividendPaymentType.value) === inputOptions.NO;
        const isPIK = Number(dividendPaymentType.value) === inputOptions.YES;

        if (dividendPaymentType) {
          if (isCash) {
            resetCellConfig();
            cell.hidden = true;
            cell.value = null;
          }

          if (isPIK) {
            setAsReadOnly();
          }
        }
      } else {
        setAsHidden(true);
      }
    } else {
      setAsHidden(true);
    }
  }

  // 08.  Shares Fully Diluted (as converted)
  if (alias === CAPTABLE.SHARES_FULLY_DILUTED_ALIAS) {
    const sharesOutstanding = capCells[cell.columnLegend! + CAPTABLE.SHARES_OUTSTANDING_ROW_NUMBER];

    if (isSecurityType(UNISSUED)) {
      setAsEditable();
      setAsRequired(true);
      cell.minValue = 1;
      cell.value = (cell.value as number) > 0 ? cell.value : '';
      cell.expr = cell.value as number;
    } else if (isSecurityType(CONVERTIBLE)) {
      cell.minValue = null;
      cell.dialog = {
        content: (
          <ConvertibleNote cell={cell as ConvertibleNotesLedgerCell} closeDialog={() => {}} isDisabled={isDisabled} />
        ),
        maxWidth: 'lg',
      };
      cell.disableEvents = true;
      cell.tooltipMessages = tooltipMessages;
      cell.readOnly = isDisabled;
    } else {
      cell.minValue = 0;
      cell.value = sharesOutstanding?.value === '' ? '' : cell.value;
      cell.dialog = null;
      setAsReadOnly();
      setAsRequired(false);
    }
  }

  // 09. Current Ownership
  if (alias === CAPTABLE.CURRENT_OWNERSHIP_ALIAS) {
    if (isSecurityType(UNISSUED)) {
      setAsReadOnly();
      cell.value = 0;
    } else {
      setAsReadOnly();
    }
  }

  // 11. Strike Price
  if (alias === CAPTABLE.STRIKE_PRICE_ALIAS) {
    // Hide cells when undelying is empty
    if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(PREFERRED)) {
        setAsEditable();
        cell.greaterThan = 0;
        setAsRequired();
      }
      if (isUnderlyingSecurity(COMMON)) {
        if (!columnId && columnId !== 0) {
          addTooltipMessage('This cell is part of a virtual column');
        }

        setAsReadOnly();

        cell.forceComponent = true;
        cell.disableEvents = true;
        cell.dialog = {
          content: <OptionsLedger cell={cell as OptionsLedgerCell} closeDialog={() => {}} />,
        };
        cell.greaterThan = 0;
        cell.readOnly = isDisabled;
        setAsRequired();
      }
      if (!isUnderlyingSecurity(PREFERRED) && !isUnderlyingSecurity(COMMON)) {
        setAsHidden();
        cell.greaterThan = null;
        setAsRequired(false);
      }
    } else {
      setAsHidden();
      cell.greaterThan = null;
    }
  }

  // 12.  Underlying Security
  if (alias === CAPTABLE.UNDERLYING_SECURITY_ALIAS) {
    if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      const underlyingSecurities = getOptions({
        securityType: isSecurityType(WARRANT) ? WARRANT : OPTION,
        cells: capCells,
      });

      if (isEmpty(underlyingSecurities)) {
        const preferredOrCommonMsg = `There are no "${LIST[PREFERRED - 1]}" or "${LIST[COMMON - 1]}" securities`;
        const commonMsg = `There are no "${LIST[COMMON - 1]}" securities`;

        // Clean previous tooltips
        removeTooltipMessage(preferredOrCommonMsg);
        removeTooltipMessage(commonMsg);

        const message = isSecurityType(WARRANT) ? preferredOrCommonMsg : commonMsg;
        addTooltipMessage(message);

        cell.value = '0';
        cell.hidden = false;
        cell.readOnly = isDisabled;
        cell.forceComponent = true;
        cell.options = [];
        cell.className = addClassName(className ?? '', 'warning');
      } else {
        // Verify if the Underlying Security selected is available
        const isListed = underlyingSecurities.find(security => toString(security.id) === toString(value as number));

        cell.value = !isListed ? null : value;
        cell.hidden = false;
        cell.readOnly = isDisabled;
        cell.forceComponent = true;
        cell.tooltipMessages = [];
        cell.options = underlyingSecurities as unknown as SecurityModel[];
        cell.className = removeClassName(className ?? '', 'warning');
      }
    } else {
      cell.hidden = true;
      cell.readOnly = true;
      cell.forceComponent = false;
      cell.tooltipMessages = [];
      cell.className = removeClassName(className ?? '', 'warning');
    }
  }

  // 14.  Liquidation Preference
  if (alias === CAPTABLE.LIQUIDATION_PREFERENCE_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      setAsEditable();
      cell.minValue = 0;
      setAsRequired();
    } else if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(PREFERRED)) {
        setAsReadOnly();
      } else if (isUnderlyingSecurity(COMMON)) {
        setAsHidden();
      }
    } else {
      setAsHidden();
      cell.minValue = 0;
    }
  }

  // 15.  Seniority
  if (cell.alias === CAPTABLE.SENIORITY_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      cell.dataEditor = (props: any) => <LiquidationPriority options={seniorityOptions(props)} {...props} />;
      cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
      cell.readOnly = isDisabled;
      cell.hidden = false;
      setAsRequired();
    } else if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(PREFERRED)) {
        setAsReadOnly();
      } else if (isUnderlyingSecurity(COMMON)) {
        setAsHidden();
      }
    } else {
      setAsHidden();
      setAsRequired(false);
      cell.minValue = 0;
    }
  }

  // 16.  Participating
  if (alias === CAPTABLE.PARTICIPATION_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      setAsRequired();
      cell.hidden = false;
      cell.readOnly = isDisabled;
      cell.forceComponent = true;
      cell.dataEditor = (props: any) => <GridSelect options={inputOptions.YES_NO} {...props} />;
      cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
    } else if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(PREFERRED)) {
        setAsRequired(false);
        cell.readOnly = true;
      }
      setAsHidden();
    } else {
      setAsHidden();
    }
  }

  // 17.  Participation Cap
  if (alias === CAPTABLE.IS_PARTICIPATION_CAP_ALIAS) {
    const participating = capCells[columnLegend! + CAPTABLE.PARTICIPATION_ROW_NUMBER];
    const isParticipating = participating && toString(participating.value as string) === '1';

    if (isParticipating) {
      if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
        setAsRequired();
        cell.hidden = false;
        cell.readOnly = isDisabled;
        cell.forceComponent = true;
        cell.dataEditor = (props: any) => <GridSelect options={inputOptions.YES_NO} {...props} />;
        cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
      }
      if (isSecurityType(OPTION) || isSecurityType(WARRANT)) {
        if (isUnderlyingSecurity(PREFERRED)) {
          cell.readOnly = true;
          setAsRequired(false);
        }
        setAsHidden();
      }
    } else {
      setAsHidden();
    }
  }

  // 18.  Participation Cap (X)
  if (alias === CAPTABLE.PARTICIPATION_CAP_ALIAS) {
    const participating = capCells[columnLegend! + CAPTABLE.PARTICIPATION_ROW_NUMBER];
    const participatingCap = capCells[columnLegend! + CAPTABLE.IS_PARTICIPATION_CAP_ROW_NUMBER];
    const isParticipating = participating && toString(participating.value as string) === '1';
    const isParticipatingCap = participatingCap && toString(participatingCap.value as string) === '1';

    if (isParticipating && isParticipatingCap) {
      if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
        setAsEditable();
      } else if (isSecurityType(OPTION) || isSecurityType(WARRANT)) {
        setAsReadOnly();
        setAsHidden();
        cell.expr = defaultValue as number;
        cell.value = defaultValue as number;
      }
    } else {
      setAsHidden();
      cell.expr = defaultValue as number;
      cell.value = defaultValue as number;
    }
  }

  // 19.  Cumulative Dividends
  if (alias === CAPTABLE.CUMULATIVE_DIVIDENDS_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      cell.hidden = false;
      cell.readOnly = isDisabled;
      cell.dataEditor = (props: any) => <GridSelect options={inputOptions.YES_NO} {...props} />;
      cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
      setAsRequired();
    } else {
      setAsHidden();
    }
  }

  // 20.  Multiple Investment Dates?
  if (alias === CAPTABLE.HAS_MULTIPLE_INVESTMENT_DATES_ALIAS) {
    if (isSecurityType(PREFERRED)) {
      const multipleInvestmentsKey = columnLegend! + CAPTABLE.MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER;
      const hasMultipleInvestments = Boolean(Number(cell.value));
      const { securities } = tableData;

      const securityWithMultipleInvestments = ((Array.isArray(securities) &&
        securities.find(security => security.order === cell.columnOrder)) ||
        {}) as SecurityModel;
      const multipleInvestments = isEmpty(capCells[multipleInvestmentsKey].value)
        ? securityWithMultipleInvestments.multiple_investments
        : capCells[multipleInvestmentsKey].value;

      // Toggle tooltip and warning based on the presence of multiple investments
      if (hasDividends && hasMultipleInvestments && (isEmpty(multipleInvestments) || multipleInvestments.length < 2)) {
        cell.className = addClassName(cell.className ?? '', 'warning');
        addTooltipMessage(validationMessages.MIN_INVESTMENTS);
      }
      if (!hasDividends || !hasMultipleInvestments || multipleInvestments?.length >= 2) {
        removeTooltipMessage(validationMessages.MIN_INVESTMENTS);
        cell.className = removeClassName(cell.className ?? '', 'warning');
      }

      // tooltip for liquidation preference ledger in case of multiple investments
      const liqPrefTooltip = hasMultipleInvestments
        ? ['Multi-date support pending: Liquidation preference for complex schedules must be calculated manually.']
        : null;
      Object.values(capCells)
        .filter(cell => cell.alias.includes('fund_liquidation_preference'))
        .forEach(liqPrefCell => {
          liqPrefCell.tooltipMessages = liqPrefTooltip;
        });

      if (hasDividends) {
        cell.readOnly = isDisabled;
        cell.hidden = false;
        cell.dialog = {
          content: <MultipleInvestmentDates cell={cell as MultipleInvestmentDatesCell} closeDialog={() => {}} />,
        };
        cell.dataEditor = (props: any) => <GridSelect options={inputOptions.YES_NO} {...props} />;
        cell.valueViewer = (props: any) => (
          <GridDialogValueViewer isDisabled={isDisabled} options={inputOptions.YES_NO} {...props} />
        );
        setAsRequired();
        cell.disableEvents = true;
        cell.skipInitialValidation = true;
      } else {
        setAsHidden();
        cell.tooltipMessages = tooltipMessages;
        cell.expr = defaultValue as number;
        cell.value = defaultValue as number;
      }
    } else if (isSecurityType(CONVERTIBLE)) {
      cell.value = getValue() as CellValue;
      cell.dialog = {
        content: (
          <ConvertibleNote cell={cell as ConvertibleNotesLedgerCell} closeDialog={() => {}} isDisabled={isDisabled} />
        ),
        maxWidth: 'lg',
      };
      cell.minValue = null;
      cell.disableEvents = true;
      cell.tooltipMessages = tooltipMessages;
      cell.dbType = 'string';
      cell.readOnly = isDisabled;
      cell.disableWhenLedgerOpen = false;
    } else {
      setAsHidden();
      cell.tooltipMessages = tooltipMessages;
      cell.expr = defaultValue as number;
      cell.value = defaultValue as number;
    }
  }

  // 21.  Cash or PIK
  if (alias === CAPTABLE.DIVIDEND_PAYMENT_TYPE_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      setAsEditable();
      if (hasDividends) {
        cell.dataEditor = (props: any) => <GridSelect options={inputOptions.DIVIDEND_PAYMENT_TYPE} {...props} />;
        cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
        setAsRequired();
        cell.skipInitialValidation = true;
      } else {
        setAsHidden();
        cell.tooltipMessages = tooltipMessages;
        cell.expr = defaultValue as number;
        cell.value = defaultValue as number;
      }
    } else {
      setAsHidden();
      cell.tooltipMessages = tooltipMessages;
      cell.expr = defaultValue as number;
      cell.value = defaultValue as number;
    }
  }

  // 22.  Dividend Rate
  if (alias === CAPTABLE.DIVIDEND_RATE_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      if (hasDividends) {
        setAsEditable();
        // when had multiple div rates or variable liq pref and deactivated through the variable chip
        if (
          cell.value === VARIABLE_VALUE &&
          !tableData.has_dividend_rate_ledger &&
          !tableData.has_liquidation_preference_ledger
        ) {
          cell.value = cell.defaultValue as CellValue;
        }
      } else {
        setAsHidden();
      }
    } else {
      setAsHidden();
    }
  }

  // 23.  Compounding
  if (alias === CAPTABLE.COMPOUNDING_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      if (hasDividends) {
        setAsEditable();
        setAsRequired();
        cell.dataEditor = (props: any) => <GridSelect options={inputOptions.YES_NO} {...props} />;
        cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
        cell.skipInitialValidation = true;
      } else {
        setAsHidden();
      }
    } else {
      setAsHidden();
    }
  }

  // 24.  Compounding Periods/Year
  if (alias === CAPTABLE.COMPOUNDING_PERIOD_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      if (hasDividends) {
        const hasCompounding = capCells[columnLegend! + CAPTABLE.COMPOUNDING_ROW_NUMBER];

        if (hasCompounding && Number(hasCompounding.value) === inputOptions.YES && !hasDivRateLedgerItems) {
          setAsEditable();
        } else if (hasCompounding && Number(hasCompounding.value) && hasDivRateLedgerItems) {
          cell.readOnly = true;
        } else {
          setAsHidden();
          cell.expr = '1';
          cell.value = '1';
        }
      } else {
        setAsHidden();
        cell.expr = '0';
        cell.value = '0';
      }
    } else {
      setAsHidden();
      cell.expr = '0';
      cell.value = '0';
    }
  }

  // 25.  Accrued Dividends (as of 12/31/2018)
  if (alias === CAPTABLE.ACCRUED_DIVIDENDS_ALIAS) {
    setAsHidden(true);
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      const cumulativeDividendsKey = columnLegend! + CAPTABLE.CUMULATIVE_DIVIDENDS_ROW_NUMBER;
      const cumulativeDividendsCell = capCells[cumulativeDividendsKey];
      const cumulativeDividendsValue = cumulativeDividendsCell?.value;
      const hasCumulativeDividends = toString(cumulativeDividendsValue as string) === '1';

      if (hasCumulativeDividends) {
        if (hasMultipleInvestments) {
          setAsReadOnly();
          cell.dialog = {
            content: <MultipleInvestmentDates cell={cell as MultipleInvestmentDatesCell} closeDialog={() => {}} />,
          };
          cell.disableEvents = true;
        } else {
          setAsReadOnly();
          cell.dialog = null;
          cell.className = removeClassName(className ?? '', 'read-only--white');
        }
      }
    }
  }

  // 26.  Initial Liquidation Preference
  if (alias === CAPTABLE.INITIAL_LIQUIDATION_PREFERENCE_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      setAsReadOnly();
    } else {
      setAsHidden(true);
    }
  }

  // 27.  Total Preference
  if (alias === CAPTABLE.TOTAL_PREFERENCE_ALIAS) {
    if (isSecurityType(PREFERRED) || (isSecurityType(CONVERTIBLE) && hasConvertibleNoteAsPreferred())) {
      cell.hidden = false;
    } else {
      setAsHidden(true);
    }
  }

  if (
    alias === CAPTABLE.LIQUIDATION_PREFERENCE_ALIAS &&
    tableData.has_liquidation_preference_ledger &&
    isSecurityType(PREFERRED) &&
    !hasMultipleInvestments
  ) {
    cell.dialog = {
      content: (
        <LiquidationPreferenceLedgerDialog
          cell={cell as ICell}
          closeDialog={() => {}}
          isDisabled={!!isDisabled}
          onInit={() => {
            cell.readOnly = true;
          }}
          tableData={tableData as any}
        />
      ),
    };
    cell.hidden = false;
    // if there is a ledger disable the liquidation preference input as the ledger dictates the value
    const ledgerExists = !!capCells[columnLegend! + CAPTABLE.LIQUIDATION_PREFERENCE_LEDGER_ROW_NUMBER]?.value;

    cell.readOnly = ledgerExists;
  }

  // if the ledger is enabled then disable the cells that are influenced by the ledger
  if (
    alias === CAPTABLE.LIQUIDATION_PREFERENCE_LEDGER_ALIAS &&
    tableData.has_liquidation_preference_ledger &&
    isSecurityType(PREFERRED) &&
    !hasMultipleInvestments
  ) {
    const ledgerValue = value as LiqPrefLedgerConfig;
    if (!ledgerValue?.threshold_type) {
      return cell;
    }

    const ledgerInfluencedCells = CAPTABLE.liqPrefLedgerInfluencedCellAliases.map(
      ({ rowNumber }) => capCells[columnLegend! + rowNumber]
    );

    // disable eslint for this line as we want to mutate the cells in memory
    /* eslint-disable no-return-assign */
    ledgerInfluencedCells.forEach(ledgerInfluencedCell => {
      const ledgerValuesForAlias = ledgerValue.items?.map(
        (ledgerItem: LiqPrefLedgerItem) => ledgerItem[ledgerInfluencedCell.alias as keyof LiqPrefLedgerItem]
      );
      const uniqueLedgerValuesForAlias = new Set(ledgerValuesForAlias);
      const allLedgerValuesAreTheSame = new Set(uniqueLedgerValuesForAlias).size === 1;

      // the tooltip is only shown if the values are not the same and after the values are filtered for null values
      ledgerInfluencedCell.readOnly = tableData.has_liquidation_preference_ledger;
      ledgerInfluencedCell.defaultTooltip = allLedgerValuesAreTheSame
        ? ''
        : uniqueLedgerValuesForAlias
          .values()
          .toArray()
          .filter(i => i !== null)
          .map((i: any) => +i)
          .join(', ');

      // for boolean dbTypes continue with default value viewer
      if (!allLedgerValuesAreTheSame && ledgerInfluencedCell.dbType !== 'boolean') {
        ledgerInfluencedCell.valueViewer = (props: any) => <VariableValueViewer {...props} />;
      }

      // need to set the liq pref ledger cell to readonly if the ledger is open
      ledgerInfluencedCell.dialog = {
        content: (
          <LiquidationPreferenceLedgerDialog
            cell={cell as ICell}
            isDisabled={!!isDisabled}
            closeDialog={() => {}}
            tableData={tableData as any}
          />
        ),
      };
    });
  }

  if (
    alias === CAPTABLE.DIVIDEND_RATE_ALIAS &&
    tableData.has_dividend_rate_ledger &&
    isSecurityType(PREFERRED) &&
    hasDividends &&
    !hasMultipleInvestments
  ) {
    cell.dialog = {
      content: (
        <DividendRateLedger
          cell={cell as ICell}
          closeDialog={() => {}}
          tableData={tableData as any}
          onInit={() => {
            cell.readOnly = true;
          }}
        />
      ),
    };
    cell.hidden = false;
    cell.readOnly = hasDivRateLedgerItems;
    if (cell.value === VARIABLE_VALUE) {
      cell.gridType = STRING;
      cell.format = undefined;
    } else {
      cell.gridType = PERCENTAGE;
      cell.format = capTableTwoDecimalPercentFormat;
    }
  }

  if (cell.citationReferences && cell.citationReferences.length > 0) {
    // Only wrap with CitationValueViewer if not already wrapped
    // Check if the current valueViewer is already CitationValueViewer to avoid recursive component stack
    const isAlreadyWrapped =
      cell.valueViewer?.name === 'CitationValueViewer' || (cell.valueViewer as any)?.isCitationViewer;

    if (!isAlreadyWrapped) {
      // Only pass accepted citations to CitationValueViewer.
      // Pending citations are handled exclusively by the AI finding popover.
      const citationsForViewer = cell.citationReferences.filter(c => c.is_accepted !== false);

      if (citationsForViewer.length > 0) {
        const originalValueViewer = cell.valueViewer;
        const wrappedViewer = (props: any) => (
          <CitationValueViewer
            value={props.value}
            citationReferences={citationsForViewer}
            originalValueViewer={originalValueViewer}
            {...props}
          />
        );
        // Mark this as a CitationValueViewer to prevent double-wrapping
        (wrappedViewer as any).isCitationViewer = true;
        cell.valueViewer = wrappedViewer;
      }
    }
  }

  if (cell.aiFieldMetadata && !cell.aiFieldMetadata.is_accepted) {
    const isAlreadyWrapped = (cell.valueViewer as any)?.isAIFindingViewer;

    if (!isAlreadyWrapped) {
      const originalValueViewer = cell.valueViewer;
      const fieldLabel = cell.alias
        ? cell.alias.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : 'Field';

      const aiVal = cell.aiFieldMetadata.value;
      const currentVal = cell.value;
      const hasCurrentValue = currentVal !== null && currentVal !== undefined && currentVal !== '';
      const hasMergeDiff = aiVal !== undefined && hasCurrentValue && String(aiVal) !== String(currentVal);

      const wrappedViewer = hasMergeDiff
        ? (props: any) => (
          <AIMergeValueViewer
            value={props.value}
            aiFieldMetadata={cell.aiFieldMetadata!}
            aiAccuracyConfidence={cell.aiAccuracyConfidence}
            fieldLabel={fieldLabel}
            securityId={cell.columnId}
            fieldAlias={cell.alias}
            originalValueViewer={originalValueViewer}
            {...props}
          />
        )
        : (props: any) => (
          <AIFindingValueViewer
            value={props.value}
            aiFieldMetadata={cell.aiFieldMetadata!}
            aiAccuracyConfidence={cell.aiAccuracyConfidence}
            fieldLabel={fieldLabel}
            securityId={cell.columnId}
            fieldAlias={cell.alias}
            originalValueViewer={originalValueViewer}
            {...props}
          />
        );
      (wrappedViewer as any).isAIFindingViewer = true;
      cell.valueViewer = wrappedViewer;
    }

    if (!cell.isAiGeneratedColumn) {
      cell.className = addClassName(cell.className ?? '', 'cell-ai-suggested');
    }
  }

  if (cell.isAiGeneratedColumn) {
    cell.className = addClassName(cell.className ?? '', 'cell-ai-generated-column');
  }

  return cell;
};

export default conditions;
